import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import '../styles/CadencePromise.css';
import '../styles/puzzle-3d.css';
// 3-Step Process flip-card styles live in WhyWeExist.css. Imported here
// so the moved-in section renders correctly (CSS is global anyway).
import '../styles/WhyWeExist.css';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ServiceTransformCards from '../components/ServiceTransformCards';
import LuxeCompassWatermark from '../components/LuxeCompassWatermark';
import PuzzlePiecesReveal from '../components/PuzzlePiecesReveal';
import '../styles/PuzzlePiecesReveal.css';
import VideoModal from '../components/VideoModal';
import '../styles/VideoModal.css';

// PAGE 4: WHY CADENCE
// Restructured: Empathy-first flow for potential clients
// RULES: Eggplant = WHITE text, Brass/Copper = BLACK text

// Founder-uploaded brand videos (2026-02). Mapping verified by watching
// each file:
//   • promise-pieces.mp4 — the LISTEN/INVESTIGATE/TRANSLATE/ADVOCATE
//     puzzle-pieces video → opens from the pieces puzzle hero.
//     Replaced 2026-02-08 with the new Gemini-generated puzzle build
//     (gemini_generated_video_22ca435b.mp4).
//   • promise-difference.mp4 — the Military Expertise / Credentials /
//     Time / Transparency differentiator video → opens from the pie chart.
const PIECES_VIDEO_URL = '/local-images/promise-pieces.mp4';
const DIFFERENCE_VIDEO_URL = '/local-images/promise-difference.mp4';

const ServicesPage = () => {
  const [activePieSlice, setActivePieSlice] = useState(null);
  // openVideo: null | 'pieces' | 'difference'
  const [openVideo, setOpenVideo] = useState(null);
  const location = useLocation();
  
  useEffect(() => {
    setActivePieSlice(null);
    setOpenVideo(null);
  }, [location.pathname]);

  // Toggle a body class whenever the pie tooltip is open. Mirrors the
  // CSS `body:has(.pie-tooltip-overlay)` rule but with a JS fallback
  // so the curtain arrows reliably hide on every browser regardless
  // of `:has()` support (founder feedback 2026-02: "I still have the
  // arrows on top of the disc"). Cleanup ensures we don't leak the
  // class on unmount.
  useEffect(() => {
    if (activePieSlice !== null) {
      document.body.classList.add('pie-tooltip-open');
    } else {
      document.body.classList.remove('pie-tooltip-open');
    }
    return () => {
      document.body.classList.remove('pie-tooltip-open');
    };
  }, [activePieSlice]);
  
  const handlePieSliceClick = (sliceIndex) => {
    setActivePieSlice(activePieSlice === sliceIndex ? null : sliceIndex);
  };
  
  const closePieTooltip = () => {
    setActivePieSlice(null);
  };

  return (
    <div className="services-page-container">
      <SEO
        title="The CADENCE Promise — Why We Exist"
        description="Our promise to every patient: clinical advocacy, decisive empowerment, and navigating care with expertise. Learn what makes CADENCE different from concierge medicine and remote monitoring."
        path="/cadence-promise"
      />
      {/* 0. EMBLEM */}
      <section className="emblem-section" data-testid="services-emblem">
        <div className="container">
          <div className="emblem-wrapper">
            <img 
              src="/cadence-gold-foil.png" 
              alt="CADENCE Medical Consulting" 
              className="velvet-emblem-home" 
            />
          </div>
        </div>
      </section>

      {/* 1. TITLE */}
      <section className="unified-content-section" data-testid="about-cadence-section">
        <div className="container">
          <LuxeCompassWatermark />
          <h1 className="unified-page-title">The CADENCE Promise</h1>
        </div>
      </section>

      {/* 1b. THE PROMISE STATEMENT - Bridge. The hover sub-lines now
          live on the puzzle artwork below (founder spec 2026-02:
          "the italics are for the image itself on the keywords"). */}
      <section className="unified-quote-section" data-testid="promise-statement">
        <div className="container">
          <p className="unified-page-quote promise-statement-copy" style={{ fontStyle: 'normal', fontWeight: 700 }}>
            We will never rush your care, dismiss your concerns, or skim your records.{' '}
            <span className="promise-verb">We&nbsp;Listen.</span>{' '}
            <span className="promise-verb">We&nbsp;Investigate.</span>{' '}
            <span className="promise-verb">We&nbsp;Translate.</span>{' '}
            <span className="promise-verb">We&nbsp;Advocate.</span>
          </p>
        </div>
      </section>

      {/* 5. HERO MOMENT — "From disorder to direction. We put the pieces together."
          Per user 2026-05-17: image first, then the eyebrow + bold gold headline
          sit BENEATH the puzzle visual as the punchline (the picture sets up the
          phrase, then the phrase lands). Subhead + clarity CTA follow. */}
      <section className="family-quote-section pieces-hero" data-testid="family-quote">
        {/* Wrapper anchors the "Watch the Story" gold pill button to
            the bottom-right of the puzzle visual. The puzzle keeps its
            own tap-to-replay behavior; the play button is a separate
            affordance for the founder video. */}
        <div className="puzzle-with-video">
          <PuzzlePiecesReveal
            src="/cadence-pieces-puzzle.png"
            alt="Listen · Investigate · Translate · Advocate — the four CADENCE puzzle pieces forming a complete picture"
            className="pieces-puzzle-visual"
            testId="pieces-puzzle-visual-wrapper"
            hotspots={[
              // Coordinates expressed as % of the image bounding box,
              // calibrated against /cadence-pieces-puzzle.png (founder
              // screenshot 2026-02). x/y = top-left corner of hitbox.
              { label: 'LISTEN', subline: "For what's unspoken and unresolved.", x: 12, y: 33, w: 22, h: 9 },
              { label: 'INVESTIGATE', subline: 'Until the missing pieces surface.', x: 43, y: 43, w: 26, h: 9 },
              { label: 'TRANSLATE', subline: 'Into language that makes sense for your life.', x: 14, y: 53, w: 30, h: 9 },
              { label: 'ADVOCATE', subline: 'For the care you need.', x: 49, y: 64, w: 24, h: 9 },
            ]}
          />
          <button
            type="button"
            className="puzzle-play-affordance"
            onClick={() => setOpenVideo('pieces')}
            aria-label="Watch the CADENCE pieces story video"
            data-testid="play-pieces-video"
          >
            <span className="play-icon" aria-hidden="true">
              <Play size={12} fill="currentColor" />
            </span>
            Watch the Story
          </button>
        </div>
        <p className="pieces-hero-eyebrow">From Disorder to Direction.</p>
        <h2 className="pieces-hero-headline">
          We Put the Pieces&nbsp;Together.
        </h2>
        <Link to="/our-story" className="read-story-link pieces-hero-cta" data-testid="read-story-link">
          Start Your Clarity Journey
          <ArrowRight size={14} />
        </Link>
      </section>

      {/* 5b. SEE HOW IT WORKS — restored 2026-02 per founder: "I like
          the location prior to the journey — the first one — I just
          wanted the UI to be fixed." Tightened copy (no orphan word),
          tighter copper-edged chassis (small, refined), and the round
          arrow medallion picks up a copper edge to anchor the eye
          before the heritage plaque further down. */}
      {/* 5b. SEE HOW IT WORKS — 2026-02 founder follow-up: "still
          two SHIW tiles on the page. I like the styling of the last
          one but I want it in the location of the first one. No
          reason for both." Heritage plaque chassis moved up here;
          the duplicate at the bottom of the page is removed. */}
      <section className="cs-howitworks-section promise-howitworks-section" data-testid="promise-howitworks">
        <div className="container">
          <Link
            to="/demo"
            state={{ from: '/cadence-promise', fromLabel: 'CADENCE Promise' }}
            className="story-fivew-plaque problem-plaque cs-howitworks-plaque"
            data-testid="promise-howitworks-btn"
            aria-label="See how CADENCE works — begin the tour"
          >
            <div className="story-fivew-plaque-inner">
              <span className="story-fivew-eyebrow">Step Inside</span>
              <h2 className="story-fivew-title problem-plaque-title">See How It Works</h2>
              <span className="problem-plaque-subline">
                A four-stage walk-through of the&nbsp;CADENCE&nbsp;experience.
              </span>
              <span className="story-fivew-plaque-hint">Begin the Tour &rarr;</span>
            </div>
          </Link>
        </div>
      </section>

      {/* The 4-stage flip-card "journey" that lived here (2026-02) was
          removed because it duplicated the cinematic walkthrough on
          /demo that the "See How It Works" tile above already links
          to. Promise now owns philosophy + differentiator; /demo owns
          the experience; /pricing owns the customer-action steps. */}

      {/* 7. WHAT MAKES CADENCE DIFFERENT - Pie Chart (moved BELOW the
          3-step process per user 2026-02 — the "how" now precedes the
          differentiator, which is the natural reading order). */}
      <section className="what-makes-different-section" data-testid="what-makes-different">
        <div className="container">
          <h2 className="section-title-white">What Makes CADENCE Different?</h2>
          
          {/* SINGLE-IMAGE PUZZLE — uses the user-supplied transparent PNG
              as one cohesive disc (no seam), with 4 INVISIBLE tap zones
              overlaid on each quadrant. Tapping any quadrant pops the
              existing plum medallion tooltip in the center. No piece
              lift/tilt animation — the puzzle stays assembled.
              The "Watch the Story" play pill is anchored to the
              bottom-right corner so it doesn't overlap the tap zones. */}
          <div
            className={`puzzle-3d puzzle-3d--single ${activePieSlice !== null ? `puzzle-3d--active-${activePieSlice}` : ''}`}
            data-testid="puzzle-3d-chart"
          >
            <img
              src="/cadence-puzzle-3d.png"
              alt="What makes CADENCE different — military medical expertise, advanced credentials, time you can feel, transparent process"
              className="puzzle-3d-image"
              draggable="false"
            />
            <button
              type="button"
              className="puzzle-3d-zone puzzle-3d-zone--tl"
              onClick={() => handlePieSliceClick(0)}
              aria-label="Military Medical Expertise — tap to learn more"
              data-testid="puzzle-piece-tl"
            />
            <button
              type="button"
              className="puzzle-3d-zone puzzle-3d-zone--tr"
              onClick={() => handlePieSliceClick(1)}
              aria-label="Advanced Credentials — tap to learn more"
              data-testid="puzzle-piece-tr"
            />
            <button
              type="button"
              className="puzzle-3d-zone puzzle-3d-zone--bl"
              onClick={() => handlePieSliceClick(2)}
              aria-label="Time You Can Feel — tap to learn more"
              data-testid="puzzle-piece-bl"
            />
            <button
              type="button"
              className="puzzle-3d-zone puzzle-3d-zone--br"
              onClick={() => handlePieSliceClick(3)}
              aria-label="Transparent Process — tap to learn more"
              data-testid="puzzle-piece-br"
            />
            {/* Pie chart "Watch the Story" pill REMOVED (2026-02) until
                the new merged mission video is ready. The differentiator
                video felt off-brand for the page's current mission-led
                story (Video 2 carries that on the puzzle above). Restore
                this button when the new video URL is supplied:

                <button
                  type="button"
                  className="puzzle-play-affordance"
                  onClick={(e) => { e.stopPropagation(); setOpenVideo('difference'); }}
                  aria-label="Watch the CADENCE difference video"
                  data-testid="play-difference-video"
                >
                  <span className="play-icon" aria-hidden="true">
                    <Play size={12} fill="currentColor" />
                  </span>
                  Watch the Story
                </button> */}
          </div>
          
          {activePieSlice !== null && (
            <div className="pie-tooltip-overlay" onClick={closePieTooltip} data-testid="pie-tooltip-overlay">
              <div className="pie-tooltip-circle" onClick={(e) => e.stopPropagation()}>
                <span className="pie-tooltip-hint">tap anywhere to close</span>
                {activePieSlice === 0 && (
                  <>
                    <div className="pie-tooltip-title">Military Medical&nbsp;Expertise</div>
                    <div className="pie-tooltip-desc">36+ years of military medical service. We trained where mistakes cost lives &mdash; that discipline goes into every record we read.</div>
                  </>
                )}
                {activePieSlice === 1 && (
                  <>
                    <div className="pie-tooltip-title">Advanced Credentials</div>
                    <div className="pie-tooltip-desc">DNP, FNP-C, WHNP-BC with 7 advanced degrees combined between our founders.</div>
                  </>
                )}
                {activePieSlice === 2 && (
                  <>
                    <div className="pie-tooltip-title">Time You Can Feel</div>
                    <div className="pie-tooltip-desc">Our case reviews take hours, not minutes &mdash; because that&rsquo;s what your story deserves. Every page gets&nbsp;read.</div>
                  </>
                )}
                {activePieSlice === 3 && (
                  <>
                    <div className="pie-tooltip-title">Transparent Process</div>
                    <div className="pie-tooltip-desc">No hidden fees. No surprises. Just clear, upfront pricing and honest advocacy.</div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* "Service Transform Cards" / "Difference, Service by Service" table
          relocated to /membership (auto-expanded below the 3 plum tiles)
          per user 2026-05-24. Kept import alive in case we need it elsewhere. */}

      {/* 8. COPPER TILE - "Finding Clarity in the Chaos" */}
      <section className="clarity-tile-section" data-testid="clarity-tile">
        <div className="container">
          <div className="clarity-copper-tile">
            <p className="clarity-tile-text">Finding Clarity in the Chaos</p>
          </div>
        </div>
      </section>

      {/* 9. FINAL CTA — Schedule a Call pill only. The duplicate
          "See How It Works" plaque that used to live here was
          removed 2026-02 (founder follow-up: "no reason for both
          tiles — keep the heritage chassis in the first location
          only"). */}
      <section className="slim-cta-section" data-testid="final-cta">
        <div className="container cadence-promise-cta-stack">
          <p className="cadence-promise-cta-eyebrow">Ready to be heard?</p>
          <Link to="/contact" className="slim-cta-btn" data-testid="cta-schedule-call">
            Schedule a Call
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />

      {/* Founder brand-video modal — opened from the pieces puzzle
          "Watch the Story" pill. The pie chart pill is currently hidden
          (see comment in JSX above) until the new merged mission video
          is supplied; the modal for it stays mounted so re-enabling is
          a single JSX uncomment. */}
      <VideoModal
        open={openVideo === 'pieces'}
        onClose={() => setOpenVideo(null)}
        src={PIECES_VIDEO_URL}
        title="We Put the Pieces Together"
      />
      <VideoModal
        open={openVideo === 'difference'}
        onClose={() => setOpenVideo(null)}
        src={DIFFERENCE_VIDEO_URL}
        title="What Makes CADENCE Different"
      />
    </div>
  );
};

export default ServicesPage;
